import React, { FC, useState, useCallback, useEffect } from 'react';
import type { MouseEvent } from 'react';

import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import Link from 'atoms/Link/Link';
import { StyledTableContainer } from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';

import dayjs from 'dayjs';

import { filterApplyType } from 'pages/LogsPage';
import { transformString } from 'organisms/Filter/helpers';
import { neutrals } from 'utils/styles/color';
import { sortByType } from './ActivityLogs';

export interface ActivityLogRow {
  action: string;
  created_at: string;
  id: string;
  metadata: {
    message: string;
    updated_fields: { [key: string]: string | number | boolean };
  };
  method: string;
  route: string;
  target_id: string;
  target_type: string;
  user_id: string;
  user_name: string;
}

export const initialValue = { page: 1, limit: 20, count: 0 };

const ActivityLogTable: FC<{
  loading: boolean;
  fetch: (
    f: typeof initialValue
  ) => Promise<{ logs: ActivityLogRow[]; count: number }>;
  name?: string;
  filter?: Partial<filterApplyType>;
  order?: 'asc' | 'desc';
  orderBy?: string;
  onRequestSort?: (e: MouseEvent<unknown>, property: sortByType) => void;
}> = ({ loading, fetch, name, filter, order, orderBy, onRequestSort }) => {
  const [pagination, setPagination] = useState(initialValue);
  const [data, setData] = useState<ActivityLogRow[]>([]);
  // const [activeRow, setActiveRow] = useState<number>();

  const handleChangePage = useCallback(
    async (event: unknown, newPage: number) => {
      const nextState = { ...pagination, page: newPage + 1 };

      const { logs, count } = await fetch(nextState);

      setPagination({ ...nextState, count });
      setData(logs);
    },
    [pagination, filter, name]
  );

  const handleChangeRowsPerPage = useCallback(
    async (option: number) => {
      const nextState = {
        ...pagination,
        limit: option,
        page: 1,
      };

      const { logs, count } = await fetch(nextState);

      setPagination({ ...nextState, count });
      setData(logs);
    },
    [filter, name]
  );

  // const handleRowClick = useCallback(
  //   (event, row) => {
  //     if (window.getSelection()?.type === 'Range') return;

  //     setActiveRow(activeRow === row.id ? 0 : row.id);
  //   },
  //   [activeRow]
  // );

  // const rowDetailsRenderer = useCallback(
  //   (row: ActivityLogRow) => (
  //     <ServicesWrapper>
  //       <MuiTable>
  //         <TableHead>
  //           <TableRow>
  //             <TableCell>
  //               <StyledServiceTableTitle>Message</StyledServiceTableTitle>
  //             </TableCell>
  //             <TableCell>
  //               <StyledServiceTableTitle>Updated Field</StyledServiceTableTitle>
  //             </TableCell>
  //           </TableRow>
  //         </TableHead>
  //         <TableBody>
  //           <TableRow>
  //             <TableCell>
  //               <StyledServiceTableText>
  //                 {row?.metadata?.message}
  //               </StyledServiceTableText>
  //             </TableCell>
  //             <TableCell>
  //               {Object.entries(row?.metadata?.updated_fields || {})?.map(
  //                 ([key, value]) => (
  //                   <StyledServiceTableText key={key}>
  //                     {key}: {String(value)}
  //                   </StyledServiceTableText>
  //                 )
  //               )}
  //             </TableCell>
  //           </TableRow>
  //         </TableBody>
  //       </MuiTable>
  //     </ServicesWrapper>
  //   ),
  //   []
  // );

  useEffect(() => {
    (async () => {
      const { logs, count } = await fetch({ ...pagination, page: 1 });
      setPagination({ ...pagination, count });
      setData(logs.map(setRowKey));
    })();
  }, [name, filter, order, orderBy]);

  function setRowKey(row: ActivityLogRow, index: number) {
    return { ...row, id: `${row.created_at}-${index}` };
  }

  function getActionTextColor(action: string): string {
    if (!action) return neutrals[700] as string;

    if (action?.toLocaleLowerCase()?.includes('create')) return '#6EE7B7';
    if (action?.toLocaleLowerCase()?.includes('update')) return '#F19D41';
    if (action?.toLocaleLowerCase()?.includes('patch')) return '#F19D41';
    if (action?.toLocaleLowerCase()?.includes('delete')) return '#EF4444';
    if (action?.toLocaleLowerCase()?.includes('approve')) return '#485651';
    if (action?.toLocaleLowerCase()?.includes('link')) return '#485651';

    return neutrals[700] as string;
  }

  const columns = [
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: '12%',
      render: ({ created_at }: ActivityLogRow) => (
        <StyledCellText>
          {dayjs(dayjs(created_at)).format('YYYY-MM-DD HH:mm:ss')}
        </StyledCellText>
      ),
    },
    {
      key: 'user_name',
      label: 'User',
      sortable: true,
      width: '13%',
      render: ({ user_name, user_id }: ActivityLogRow) => {
        if (!user_name) return '';

        return (
          <Link to={`/settings?user_id=${user_id}`} $textDecoration="none">
            <StyledCellText $textType="link">{user_name}</StyledCellText>
          </Link>
        );
      },
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      width: '10%',
      render: ({ action }: ActivityLogRow) => (
        <StyledCellText $color={getActionTextColor(action)}>
          {transformString(action)}
        </StyledCellText>
      ),
    },
    {
      key: 'metadata.message',
      label: 'Event',
      sortable: true,
      width: '15%',
      render: ({ metadata: { message } }: ActivityLogRow) => (
        <StyledCellText>{message}</StyledCellText>
      ),
    },
  ];

  return (
    <StyledTableContainer>
      <Table
        data={data}
        columns={columns}
        idKey="id"
        order={order}
        orderBy={orderBy}
        onRequestSort={onRequestSort}
        sx={{ minWidth: 650 }}
        aria-label="simple table"
        loading={loading}
        // collapsible
        // collapsedRow={activeRow}
        // onRowClick={handleRowClick}
        // rowDetailsRenderer={rowDetailsRenderer}
      />
      <StyledTablePagination
        labelDisplayedRows={() => null}
        rowsPerPageOptions={[]}
        count={pagination.count}
        rowsPerPage={pagination.limit}
        page={pagination.count ? pagination.page - 1 : 0}
        onPageChange={handleChangePage}
        ActionsComponent={(props) => (
          <PaginationActions
            {...props}
            labelRowsPerPage="Items per page"
            rowsPerPageOptions={[20, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      />
    </StyledTableContainer>
  );
};

export default ActivityLogTable;
